import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { Box, Typography, Button, Alert } from "@mui/material";
import { Toolbar } from "../../components/ui/toolbar/toolbar.component";
import { Action } from "../../types/toolbar";
import CustomModal from "../../components/ui/modal/modal.component";
import EquipmentGroupForm from "../../components/service/equipmentGroupForm.component";
import { useCRUD } from "../../api/crud";
import { EQUIPMENT_GROUPS_ENDPOINT } from "../../consts";
import { SUCCESS_SNACKBAR, ERROR_SNACKBAR } from "../../consts";
import { useHasPermission } from "../../contexts/auth";

export const EquipmentGroupDetailsActions = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { loading, error, remove } = useCRUD(EQUIPMENT_GROUPS_ENDPOINT);
  const [updateGroupOpened, setUpdateGroupOpened] = useState<boolean>(false);
  const [deleteGroupOpened, setDeleteGroupOpened] = useState<boolean>(false);
  const canChangeEquipmentGroup = useHasPermission("service.change_equipmentgroup");
  const canDeleteEquipmentGroup = useHasPermission("service.delete_equipmentgroup");

  const handleDeleteGroup = async () => {
    if (loading || !id) {
      return;
    }

    const result = await remove({ id });

    if (result?.success) {
      enqueueSnackbar(t('Group deleted successfully'), SUCCESS_SNACKBAR);
      setDeleteGroupOpened(false);
      navigate('/service/statistics/equipment-groups');
    } else {
      enqueueSnackbar(t('Failed to delete group'), ERROR_SNACKBAR);
    }
  };

  const actions: Action[] = [
    {
      icon: <EditIcon />,
      label: t("Update group"),
      onClick: () => {
        setUpdateGroupOpened(true);
      },
      disabled: !canChangeEquipmentGroup,
    },
    {
      icon: <DeleteIcon />,
      label: t("Delete group"),
      onClick: () => {
        setDeleteGroupOpened(true);
      },
      color: "var(--errorColor)",
      disabled: !canDeleteEquipmentGroup,
    },
  ];

  const errorMessage =
    typeof error === 'string'
      ? error
      : error?.message ?? error?.detail ?? '';

  const deleteModalContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {t('Deleting this group will permanently remove it. Equipments themselves will not be affected.')}
      </Typography>

      <Alert severity="warning">
        <Typography variant="body2">
          {t('This action cannot be undone. All associated data will be permanently deleted.')}
        </Typography>
      </Alert>

      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      <Button
        variant="contained"
        color="error"
        onClick={handleDeleteGroup}
        disabled={loading}
      >
        {loading ? t('Processing...') : t('Confirm deletion')}
      </Button>
    </Box>
  );

  return (
    <>
      <Toolbar actions={actions} />
      <CustomModal
        opened={updateGroupOpened}
        setOpened={setUpdateGroupOpened}
        title={t("Update group")}
        content={
          <EquipmentGroupForm
            id={id}
            onSuccess={() => {
              setUpdateGroupOpened(false);
            }}
          />
        }
      />
      <CustomModal
        opened={deleteGroupOpened}
        setOpened={setDeleteGroupOpened}
        title={t("Delete group")}
        content={deleteModalContent}
      />
    </>
  );
};
